import type { MotionValue } from 'framer-motion';
import type { BoxProps } from '@mui/material/Box';
import type { Theme, SxProps } from '@mui/material/styles';

import { Fragment } from 'react';
import { mergeClasses } from 'minimal-shared/utils';
import { m, useSpring, useTransform } from 'framer-motion';

import Box from '@mui/material/Box';
import Portal from '@mui/material/Portal';
import { styled, useTheme } from '@mui/material/styles';

import { createClasses } from 'src/theme/create-classes';

// ----------------------------------------------------------------------

const scrollProgressClasses = {
  circular: createClasses('scroll__progress__circular'),
  linear: createClasses('scroll__progress__linear'),
};

/** `theme.vars.palette[color].main`·`.light` 를 읽으므로 그 칸이 있는 키만 받는다 */
export type ScrollProgressColor =
  | 'inherit'
  | 'primary'
  | 'secondary'
  | 'info'
  | 'success'
  | 'warning'
  | 'error';

/**
 * 스크롤 진행도를 그린다. `useScrollProgress()` 가 준 `progress` 를 그대로 받는다.
 *
 * ⚠️ **필수 프롭은 `progress` 하나뿐이다** — 나머지는 전부 선택이다.
 *
 * ⚠️ **`variant` 는 필수가 아니다.** `'circular'` 일 때만 원을 그리고, 그 밖에는 —
 * 안 주는 것을 포함해 — 가로 막대를 그린다(`agreement-content-view` 가 그렇게 쓴다).
 *
 * ⚠️ `size` 의 기본값이 갈래마다 다르다 — 원은 64, 막대는 높이 3.
 * `thickness` 는 원에서만 쓰인다.
 *
 * ⚠️ `whenScroll="x"` 는 **RTL 에서만** 뜻이 있다. 진행도를 뒤집을지 정할 뿐이고,
 * 가로/세로 중 무엇을 볼지는 `progress` 로 무엇을 넘겼는지가 정한다.
 */
export type ScrollProgressProps = {
  /**
   * 객체 하나도 되고 **테마 콜백 배열**도 된다. ⚠️ 제네릭 인자를 빼면 콜백의 `theme` 이
   * 좁혀져 `theme.zIndex` 를 읽는 호출부가 막힌다
   */
  sx?: SxProps<Theme>;
  /** 원의 지름 · 막대의 높이. 기본 원 64 · 막대 3 */
  size?: number;
  /** 참이면 `Portal` 로 body 에 띄운다 */
  portal?: boolean;
  /** 기본은 막대 */
  variant?: 'linear' | 'circular';
  /** 바깥 `Box` 에 그대로 넘긴다 */
  slotProps?: { wrapper?: BoxProps };
  className?: string;
  /** 원의 선 굵기. 기본 `3.6` */
  thickness?: number;
  /** RTL 뒤집기 판정용. 기본 `'y'` */
  whenScroll?: 'x' | 'y';
  /** 팔레트 키 또는 `'inherit'`. 기본 `'primary'` */
  color?: ScrollProgressColor;
  /** `useScrollProgress()` 의 `scrollYProgress` · `scrollXProgress` */
  progress: MotionValue<number>;
};

export function ScrollProgress({
  sx,
  size,
  portal,
  variant,
  slotProps,
  className,
  thickness = 3.6,
  whenScroll = 'y',
  color = 'primary',
  progress: progressProps,
  ...other
}: ScrollProgressProps) {
  const theme = useTheme();

  const isRtl = theme.direction === 'rtl';

  const transformProgress = useTransform(progressProps, [0, -1], [0, 1]);

  const progress = isRtl && whenScroll === 'x' ? transformProgress : progressProps;

  const scaleX = useSpring(progress, { stiffness: 100, damping: 30, restDelta: 0.001 });

  const progressSize = variant === 'circular' ? (size ?? 64) : (size ?? 3);

  const renderCircular = () => (
    <CircularRoot
      viewBox={`0 0 ${progressSize} ${progressSize}`}
      xmlns="http://www.w3.org/2000/svg"
      className={mergeClasses([scrollProgressClasses.circular, className])}
      sx={[
        {
          width: progressSize,
          height: progressSize,
          ...(color !== 'inherit' && { color: theme.vars.palette[color].main }),
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...other}
    >
      <circle
        cx={progressSize / 2}
        cy={progressSize / 2}
        r={progressSize / 2 - thickness - 4}
        strokeWidth={thickness}
        strokeOpacity={0.2}
      />

      <m.circle
        cx={progressSize / 2}
        cy={progressSize / 2}
        r={progressSize / 2 - thickness - 4}
        strokeWidth={thickness}
        style={{ pathLength: progress }}
      />
    </CircularRoot>
  );

  const renderLinear = () => (
    <LinearRoot
      className={mergeClasses([scrollProgressClasses.linear, className])}
      sx={[
        {
          height: progressSize,
          ...(color !== 'inherit' && {
            background: `linear-gradient(135deg, ${theme.vars.palette[color].light}, ${theme.vars.palette[color].main})`,
          }),
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      style={{ scaleX }}
      {...other}
    />
  );

  const PortalWrapper = portal ? Portal : Fragment;

  return (
    <PortalWrapper>
      <Box {...slotProps?.wrapper}>
        {variant === 'circular' ? renderCircular() : renderLinear()}
      </Box>
    </PortalWrapper>
  );
}

// ----------------------------------------------------------------------

const CircularRoot = styled(m.svg)(({ theme }) => ({
  transform: 'rotate(-90deg)',
  color: theme.vars.palette.text.primary,
  circle: { fill: 'none', strokeDashoffset: 0, stroke: 'currentColor' },
}));

const LinearRoot = styled(m.div)(({ theme }) => ({
  top: 0,
  left: 0,
  right: 0,
  transformOrigin: '0%',
  backgroundColor: theme.vars.palette.text.primary,
}));
